export const CAMPAIGN_REPORT_COLUMNS = [
  { id: 'type', label: 'Type' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'spend', label: 'Spend' },
  { id: 'purchases', label: 'Purchases' },
  { id: 'sales', label: 'Sales' },
  { id: 'ctr', label: 'CTR' },
  { id: 'roas', label: 'ROAS' },
] as const;

export const KEYWORD_REPORT_COLUMNS = [
  { id: 'type', label: 'Type' },
  { id: 'keyword', label: 'Keyword' },
  { id: 'match', label: 'Match' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'spend', label: 'Spend' },
  { id: 'purchases', label: 'Purchases' },
  { id: 'sales', label: 'Sales' },
  { id: 'ctr', label: 'CTR' },
  { id: 'roas', label: 'ROAS' },
] as const;

export const TARGET_REPORT_COLUMNS = [
  { id: 'target', label: 'Target ID' },
  { id: 'targeting', label: 'Targeting' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'adGroup', label: 'Ad group ID' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'spend', label: 'Spend' },
  { id: 'purchases', label: 'Purchases' },
  { id: 'sales', label: 'Sales' },
  { id: 'ctr', label: 'CTR' },
  { id: 'roas', label: 'ROAS' },
] as const;

export type CampaignReportColumnId = (typeof CAMPAIGN_REPORT_COLUMNS)[number]['id'];
export type KeywordReportColumnId = (typeof KEYWORD_REPORT_COLUMNS)[number]['id'];
export type TargetReportColumnId = (typeof TARGET_REPORT_COLUMNS)[number]['id'];

export const DEFAULT_CAMPAIGN_REPORT_COLUMN_IDS: CampaignReportColumnId[] =
  CAMPAIGN_REPORT_COLUMNS.map((column) => column.id);

export const DEFAULT_KEYWORD_REPORT_COLUMN_IDS: KeywordReportColumnId[] =
  KEYWORD_REPORT_COLUMNS.map((column) => column.id);

export const DEFAULT_TARGET_REPORT_COLUMN_IDS: TargetReportColumnId[] =
  TARGET_REPORT_COLUMNS.map((column) => column.id);

function requiredColumnIdSet<T extends string>(required?: T | readonly T[]): Set<T> {
  if (required == null) return new Set();
  return new Set(Array.isArray(required) ? required : [required]);
}

export function toggleReportColumnSelection<T extends string>(
  selected: T[],
  columnId: T,
  checked: boolean,
  requiredColumnId?: T | readonly T[]
): T[] {
  if (checked) {
    return selected.includes(columnId) ? selected : [...selected, columnId];
  }
  if (requiredColumnIdSet(requiredColumnId).has(columnId) || selected.length <= 1) {
    return selected;
  }
  return selected.filter((id) => id !== columnId);
}

export function visibleCampaignReportColumns(
  selected: CampaignReportColumnId[],
  showTypeColumn: boolean
): CampaignReportColumnId[] {
  return CAMPAIGN_REPORT_COLUMNS.map((column) => column.id).filter(
    (id) => selected.includes(id) && (id !== 'type' || showTypeColumn)
  );
}

export function visibleKeywordReportColumns(
  selected: KeywordReportColumnId[],
  showTypeColumn: boolean
): KeywordReportColumnId[] {
  return KEYWORD_REPORT_COLUMNS.map((column) => column.id).filter(
    (id) => selected.includes(id) && (id !== 'type' || showTypeColumn)
  );
}

export function visibleTargetReportColumns(selected: TargetReportColumnId[]): TargetReportColumnId[] {
  return TARGET_REPORT_COLUMNS.map((column) => column.id).filter((id) => selected.includes(id));
}

export function campaignReportColumnLabel(columnId: CampaignReportColumnId): string {
  return CAMPAIGN_REPORT_COLUMNS.find((column) => column.id === columnId)?.label ?? columnId;
}

export function keywordReportColumnLabel(columnId: KeywordReportColumnId): string {
  return KEYWORD_REPORT_COLUMNS.find((column) => column.id === columnId)?.label ?? columnId;
}

export function targetReportColumnLabel(columnId: TargetReportColumnId): string {
  return TARGET_REPORT_COLUMNS.find((column) => column.id === columnId)?.label ?? columnId;
}
